import type { ReactNode } from "react";
import { createContext, useContext, useState, useEffect } from "react";

// Hardcoded test users for development
const HARDCODED_USERS = [
  {
    id: "user_seeker_1",
    email: "test@testdomene.no",
    password: "Admin123",
    name: "Test User",
    role: "seeker" as const,
    onboardingCompleted: true,
  },
  {
    id: "user_employer_1",
    email: "employer@testdomene.no",
    password: "Admin123",
    name: "Test Employer",
    role: "employer" as const,
    onboardingCompleted: true,
  },
  {
    id: "user_admin_1",
    email: "admin@testdomene.no",
    password: "Admin123",
    name: "Test Admin",
    role: "admin" as const,
    onboardingCompleted: true,
  },
];

type User = (typeof HARDCODED_USERS)[number];

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load user from localStorage on mount
  useEffect(() => {
    const storedUser = localStorage.getItem("findr_auth_user");
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (e) {
        console.error("Failed to parse stored user", e);
        localStorage.removeItem("findr_auth_user");
      }
    }
    setIsLoading(false);
  }, []);

  const login = async (email: string, password: string): Promise<boolean> => {
    // Simulate network delay
    await new Promise((resolve) => setTimeout(resolve, 500));

    const foundUser = HARDCODED_USERS.find((u) => u.email === email && u.password === password);

    if (foundUser) {
      const userWithoutPassword = { ...foundUser };
      delete (userWithoutPassword as any).password;

      setUser(userWithoutPassword);
      localStorage.setItem("findr_auth_user", JSON.stringify(userWithoutPassword));
      return true;
    }

    return false;
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem("findr_auth_user");
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        login,
        logout,
        isLoading,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
